import {
  SQPP_COOKIE_KEY,
  readSqppForceParam
} from '~/composables/energy/use-energy-sqpp-experiment'
import { controlStepToSlug, isSqppSlug } from '~/helpers/energy-sqpp.helpers'
import { SQPP_URL } from '~/constants/funnel/energy-sqpp'

/**
 * HI-1430 — routes a bucketed visitor from a control funnel URL to the SQPP
 * variant equivalent.
 *
 * WHY MIDDLEWARE, and why a cookie:
 *
 * Every entry point in the app hardcodes `/energy/quote/form/step1/`
 * (`verticals.constants.js`, `home-page.js`, `AgeCta`, `LifeCover`, ...), and
 * Nuxt route middleware runs server-side on the initial request but does NOT
 * re-run on hydration. So the bucketing signal has to be readable on the
 * server: hence the cookie, set by the Optimizely variation's JS on the first
 * visit, plus `?sqpp=0|1` for QA.
 *
 * `routeRules` would not work — a static redirect would kill the control arm.
 * Putting it in `[step].vue` would break "control is provably untouched".
 *
 * The first visit itself is handled upstream by an Optimizely *redirect*
 * experiment, which runs from <head> before first paint. This middleware covers
 * every visit after that, plus SPA navigations, Back, and deep links.
 */

const CONTROL_STEP_PATTERN = /^\/energy\/quote\/form\/(step[0-9]+)\/?$/

export default defineNuxtRouteMiddleware(to => {
  const match = CONTROL_STEP_PATTERN.exec(to.path)

  if (!match) {
    return
  }

  const step = match[1]

  // `?sqpp=0|1` beats everything, so QA never depends on dashboard config.
  const forced = readSqppForceParam(to.query as Record<string, unknown>)

  if (forced === 0) {
    return
  }

  const isVariant = forced === 1 || useCookie(SQPP_COOKIE_KEY).value === '1'

  if (!isVariant) {
    return
  }

  // No model server-side, so this degrades to the static column — correct for
  // step1, the only step a cold entry uses. On the client the funnel store is
  // hydrated, so the ambiguous steps resolve properly.
  const model = import.meta.client ? readEnergyFunnelModel() : null
  const slug = controlStepToSlug(step, model)

  // The bill-upload steps have no variant equivalent and there was no model to
  // resolve them against — leave the visitor on control rather than guessing.
  if (!slug || !isSqppSlug(slug)) {
    return
  }

  // Query threading is load-bearing: Optimizely targeting and the funnel's own
  // `$query` persistence both depend on the params surviving the hop.
  return navigateTo(
    {
      path: `${SQPP_URL}/${slug}/`,
      query: to.query
    },
    { replace: true }
  )
})

/**
 * The flattened funnel answers, or null when the store is not populated yet.
 * Kept local to the middleware so it never pulls the store in on the server.
 */
function readEnergyFunnelModel (): Record<string, unknown> | null {
  try {
    const energyStore = useEnergyStore()
    const form = energyStore.form

    if (!form || Object.keys(form).length === 0) {
      return null
    }

    return flattenObj(form, 1)
  } catch {
    return null
  }
}
